import { BuildingOffice2Icon, GlobeAltIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { MultiValueInput } from '@/components/form/MultiValueInput';
import { useModifyAccount } from '@/hooks';
import { useAxios } from '@/hooks/useAxios';
import { SubtenantConfirmationDialog } from '@/sections/components/SubtenantConfirmationDialog';
import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import { useAuth } from '@/state/auth';
import { Account } from '@/types';

/**
 * ParentAccountPane shows the parent account for the current tenant
 * and allows praetorian users to unlink or link a parent.
 */
export const ParentAccountPane: React.FC<{
  accounts: Account[];
  customerEmail?: string;
}> = ({ accounts, customerEmail }) => {
  const { friend, me } = useAuth();
  const email = customerEmail || friend || me;
  const { dataMap: displayNameMap } = useCustomerDisplayName();
  const { mutate: unlinkAccount } = useModifyAccount('unlink', email);

  const parentAccount = useMemo(
    () => accounts.find(a => a.type === 'subtenant' && a.name === email),
    [accounts, email]
  );

  const parentEmail = parentAccount?.member;
  const parentDisplayName = parentEmail
    ? displayNameMap.get(parentEmail) || parentEmail
    : '';

  const handleUnlink = () => {
    if (!parentAccount) return;
    unlinkAccount(
      {
        username: parentAccount.member,
        value: parentAccount.value,
        key: parentAccount.key,
        member: parentAccount.member,
        config: {},
      },
      {
        onSuccess: () => {
          toast.success('Parent account unlinked');
        },
      }
    );
  };

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-linear-to-br from-purple-500/10 to-purple-600/10">
            <BuildingOffice2Icon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Parent Account</h3>
            <p className="text-chariot-text-secondary mt-0.5 text-sm">
              {parentEmail
                ? 'This account is a child of the parent account below'
                : 'Link a parent account to create a subtenant relationship'}
            </p>
          </div>
        </div>
      </div>

      {parentEmail ? (
        <div className="space-y-2">
          <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between rounded-lg border px-4 py-3">
            <div>
              <span className="text-chariot-text-primary text-sm font-medium">
                {parentDisplayName}
              </span>
              {parentDisplayName !== parentEmail && (
                <span className="text-chariot-text-secondary ml-2 text-xs">
                  {parentEmail}
                </span>
              )}
            </div>
            <button
              type="button"
              className="text-chariot-text-secondary hover:text-chariot-stroke-critical text-xs font-medium"
              onClick={handleUnlink}
            >
              Remove
            </button>
          </div>
          <ParentDomainsList parentEmail={parentEmail} />
        </div>
      ) : (
        <LinkParentForm customerEmail={email} />
      )}
    </div>
  );
};

/**
 * LinkParentForm allows praetorian users to link a parent account.
 * Shows the full list of available accounts by default, filtered on search.
 */
const LinkParentForm: React.FC<{ customerEmail: string }> = ({
  customerEmail,
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pendingParent, setPendingParent] = useState<{
    email: string;
    displayName: string;
  } | null>(null);
  const { whoami } = useAuth();
  const { dataMap: displayNameMap } = useCustomerDisplayName();
  const { mutate: linkAccount } = useModifyAccount('link', customerEmail);

  const childDisplayName = displayNameMap.get(customerEmail) || customerEmail;

  const parentOptions = useMemo(() => {
    const accountNames = whoami?.accounts ?? [];
    return accountNames
      .filter(email => email !== customerEmail)
      .map(email => ({
        email,
        displayName: displayNameMap.get(email) || email,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [whoami?.accounts, displayNameMap, customerEmail]);

  const filtered = useMemo(() => {
    if (!search) return parentOptions;
    const q = search.toLowerCase();
    return parentOptions.filter(
      o =>
        o.displayName.toLowerCase().includes(q) ||
        o.email.toLowerCase().includes(q)
    );
  }, [parentOptions, search]);

  const handleSelect = (option: { email: string; displayName: string }) => {
    setIsOpen(false);
    setSearch('');
    setPendingParent(option);
  };

  const handleConfirm = () => {
    if (!pendingParent) return;
    linkAccount(
      {
        username: pendingParent.email,
        type: 'subtenant',
        config: {},
      },
      {
        onSuccess: () => {
          toast.success('Parent account linked');
          setPendingParent(null);
        },
      }
    );
  };

  return (
    <div className="relative">
      <Input
        name="link_parent_search"
        value={search}
        onChange={e => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          // Delay to allow click on dropdown item
          setTimeout(() => setIsOpen(false), 200);
        }}
        placeholder="Search for a parent account to link..."
      />
      {isOpen && filtered.length > 0 && (
        <div className="border-chariot-stroke absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-white shadow-lg dark:bg-zinc-900">
          {filtered.map(option => (
            <button
              key={option.email}
              type="button"
              className="hover:bg-chariot-background-secondary w-full px-3 py-2 text-left"
              onMouseDown={e => {
                e.preventDefault(); // Prevent blur before click registers
                handleSelect(option);
              }}
            >
              <span className="text-chariot-text-primary text-sm">
                {option.displayName}
              </span>
              {option.displayName !== option.email && (
                <span className="text-chariot-text-secondary ml-2 text-xs">
                  {option.email}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {isOpen && filtered.length === 0 && search && (
        <div className="border-chariot-stroke bg-chariot-background absolute z-10 mt-1 w-full rounded-lg border px-3 py-2 shadow-lg">
          <p className="text-chariot-text-secondary text-sm">
            No matching accounts found
          </p>
        </div>
      )}
      <SubtenantConfirmationDialog
        isOpen={!!pendingParent}
        onConfirm={handleConfirm}
        onCancel={() => setPendingParent(null)}
        parentDisplayName={pendingParent?.displayName ?? ''}
        childDisplayName={childDisplayName}
      />
    </div>
  );
};

/**
 * ChildAccountsPane shows subtenant children of the current tenant.
 * Only visible when there are child accounts.
 */
export const ChildAccountsPane: React.FC<{
  accounts: Account[];
  customerEmail?: string;
}> = ({ accounts, customerEmail }) => {
  const { friend, me } = useAuth();
  const email = customerEmail || friend || me;
  const { dataMap: displayNameMap } = useCustomerDisplayName();
  const { mutate: unlinkAccount } = useModifyAccount('unlink', email);

  const children = useMemo(
    () => accounts.filter(a => a.type === 'subtenant' && a.member === email),
    [accounts, email]
  );

  if (children.length === 0) return null;

  const handleRemoveChild = (child: Account) => {
    unlinkAccount(
      {
        username: child.member,
        value: child.value,
        key: child.key,
        member: child.member,
        config: {},
      },
      {
        onSuccess: () => {
          toast.success('Child account unlinked');
        },
      }
    );
  };

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-linear-to-br from-purple-500/10 to-purple-600/10">
            <BuildingOffice2Icon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              Child Accounts
              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                {children.length}
              </span>
            </h3>
            <p className="text-chariot-text-secondary mt-0.5 text-sm">
              Subtenant accounts managed under this account
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {children.map(child => {
          const childEmail = child.name;
          const childDisplayName = displayNameMap.get(childEmail) || childEmail;
          return (
            <div
              key={child.key}
              className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between rounded-lg border px-4 py-3"
            >
              <div>
                <span className="text-chariot-text-primary text-sm font-medium">
                  {childDisplayName}
                </span>
                {childDisplayName !== childEmail && (
                  <span className="text-chariot-text-secondary ml-2 text-xs">
                    {childEmail}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="text-chariot-text-secondary hover:text-chariot-stroke-critical text-xs font-medium"
                onClick={() => handleRemoveChild(child)}
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * ParentDomainsList shows the allowed domains inherited from a parent account.
 */
const ParentDomainsList: React.FC<{ parentEmail: string }> = ({
  parentEmail,
}) => {
  const axios = useAxios({ email: parentEmail, doNotImpersonate: true });
  const [domains, setDomains] = useState<string[]>([]);

  useEffect(() => {
    axios
      .get<{ domains: string[] }>('/customer/domains')
      .then(res => setDomains(res.data.domains ?? []))
      .catch(() => setDomains([]));
  }, [axios, parentEmail]);

  if (domains.length === 0) return null;

  return (
    <div className="text-chariot-text-secondary flex flex-wrap items-center gap-1.5 px-1 text-xs">
      <span>Inherited domains:</span>
      {domains.map(d => (
        <span
          key={d}
          className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary rounded-md border px-2 py-0.5"
        >
          {d}
        </span>
      ))}
    </div>
  );
};

/**
 * AllowedDomainsPane shows and allows editing of the allowed email domains
 * for the current tenant. Only visible to praetorian users.
 */
export const AllowedDomainsPane: React.FC<{
  customerEmail?: string;
}> = ({ customerEmail }) => {
  const { friend, me } = useAuth();
  const email = customerEmail || friend || me;
  const axios = useAxios({ email, doNotImpersonate: true });

  const [domains, setDomains] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get<{ domains: string[] }>('/customer/domains')
      .then(res => {
        setDomains(res.data.domains ?? []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [axios, email]);

  const handleSave = useCallback(
    async (newDomains: string[]) => {
      setSaving(true);
      try {
        const res = await axios.put<{ domains: string[] }>(
          '/customer/domains',
          { domains: newDomains }
        );
        setDomains(res.data.domains ?? []);
        toast.success('Allowed domains updated');
      } catch {
        toast.error('Failed to update allowed domains');
      } finally {
        setSaving(false);
      }
    },
    [axios]
  );

  if (!loaded) return null;

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary/60 rounded-lg border p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-linear-to-br from-blue-500/10 to-blue-600/10">
          <GlobeAltIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Allowed Email Domains</h3>
          <p className="text-chariot-text-secondary mt-0.5 text-sm">
            Only collaborators with these email domains can be added to this
            account
          </p>
        </div>
      </div>
      <MultiValueInput
        values={domains}
        onChange={newDomains => {
          const normalized = newDomains.map(d => d.trim().toLowerCase());
          setDomains(normalized);
          handleSave(normalized);
        }}
        editable={!saving}
        placeholder="Enter any domain (e.g. acme.com) and press Enter"
        aria-label="Allowed email domains"
      />
      {domains.length === 0 && (
        <p className="text-chariot-text-secondary mt-1 text-xs italic">
          No domains configured. Any valid email domain can be added.
        </p>
      )}
    </div>
  );
};
